import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Divider,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import { post } from "../../services";
import {
  ADD_INVOICE_SERIES_SETTINGS,
  GET_INVOICE_SERIES_SETTINGS,
} from "../../graphql";

/* ------------------------------------------------------------------
   Helpers
------------------------------------------------------------------ */
const FieldAndValue = ({ field, value }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
    <Typography fontSize={14} fontWeight={600}>
      {field}
    </Typography>
    <Typography fontSize={14}>{value}</Typography>
  </Stack>
);

/* ------------------------------------------------------------------
   Series Editor
------------------------------------------------------------------ */
const SeriesEditor = ({ label, data, onChange, disabled }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle1" fontWeight={600}>
      {label}
    </Typography>

    {!disabled ? (
      <Stack direction="row" spacing={1}>
        <TextField
          label="Prefix"
          size="small"
          value={data?.prefix || ""}
          onChange={(e) => onChange("prefix", e.target.value)}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Start Number"
          size="small"
          type="number"
          // FIX: Use ?? instead of || to allow 0 as a valid number
          value={data?.start_number ?? ""}
          onChange={(e) =>
            onChange("start_number", Number(e.target.value))
          }
          sx={{ flex: 1 }}
        />
      </Stack>
    ) : (
      <>
        <FieldAndValue field="Prefix" value={data?.prefix || "-"} />
        <FieldAndValue field="Start Number" value={data?.start_number ?? "-"} />
        <FieldAndValue
          field="Current Number"
          value={data?.current_number ?? "-"}
        />
      </>
    )}

    <Divider sx={{ mt: 1 }} />
  </Stack>
);

/* ------------------------------------------------------------------
   MAIN COMPONENT
------------------------------------------------------------------ */
export const EditableInvoiceSeriesAccordion = ({
  hotelId,
  colors,
  gstEnabled,
  billingEnabled,
}) => {
  // ✅ Define Series based on GST status
  const HOTEL_SERIES = gstEnabled
    ? ["B2B", "B2C", "Complimentary", "Proforma"]
    : ["NON_GST", "Complimentary", "Proforma"];

  const [form, setForm] = useState({
    hotel_billing: {},
    restaurant_billing: {},
    activity_billing: {},
    advance_receipt: {},
  });

  const [dbSnapshot, setDbSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ----------------------------------------------------
       Fetch Data
     ----------------------------------------------------- */
  const loadSeries = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const apiResponse = await post("", {
        query: GET_INVOICE_SERIES_SETTINGS,
        variables: { hotel_id: hotelId },
      });

      const data =
        apiResponse?.data?.data?.get_invoice_series_settings?.data || {};

      let hotelBilling = { ...(data.hotel_billing || {}) };

      // ✅ Enforce GST / NON_GST keys logic
      if (!gstEnabled) {
        hotelBilling.NON_GST =
          hotelBilling.NON_GST || { prefix: "", start_number: 1 };
        delete hotelBilling.B2B;
        delete hotelBilling.B2C;
      } else {
        delete hotelBilling.NON_GST;
      }

      const preparedForm = {
        hotel_billing: hotelBilling,
        restaurant_billing: data.restaurant_billing || {},
        activity_billing: data.activity_billing || {},
        advance_receipt: data.advance_receipt || {},
      };

      setForm(preparedForm);
      setDbSnapshot(preparedForm);
    } catch (err) {
      toast.error("Failed to load invoice series.");
      console.error(err);
    }

    setLoading(false);
  };

  /* ----------------------------------------------------
       Validate & Save Data
     ----------------------------------------------------- */
  const saveSeries = async () => {
    setSaving(true);

    // --- VALIDATION START ---
    
    // 1. Advance Receipt (Always Mandatory)
    const advReceipt = form.advance_receipt;
    if (!advReceipt?.prefix || advReceipt?.start_number === undefined || advReceipt?.start_number === "") {
      toast.error("Advance Receipt Series (Prefix & Start Number) is mandatory.");
      setSaving(false);
      return;
    }

    // 2. Billing Enabled Specific Validations
    if (billingEnabled) {
      // 2a. Hotel Billing Checks
      if (gstEnabled) {
        const b2b = form.hotel_billing?.B2B;
        const comp = form.hotel_billing?.Complimentary;

        if (!b2b?.prefix || b2b?.start_number === undefined || b2b?.start_number === "") {
          toast.error("B2B Series is mandatory when GST is enabled.");
          setSaving(false);
          return;
        }
        if (!comp?.prefix || comp?.start_number === undefined || comp?.start_number === "") {
          toast.error("Complimentary Series is mandatory.");
          setSaving(false);
          return;
        }
      } else {
        const nonGst = form.hotel_billing?.NON_GST;
        const comp = form.hotel_billing?.Complimentary;

        if (!nonGst?.prefix || nonGst?.start_number === undefined || nonGst?.start_number === "") {
          toast.error("NON_GST Series is mandatory.");
          setSaving(false);
          return;
        }
        if (!comp?.prefix || comp?.start_number === undefined || comp?.start_number === "") {
          toast.error("Complimentary Series is mandatory.");
          setSaving(false);
          return;
        }
      }

      // 2b. Restaurant Billing Check (Mandatory if Billing Enabled)
      const restBilling = form.restaurant_billing;
      if (!restBilling?.prefix || restBilling?.start_number === undefined || restBilling?.start_number === "") {
        toast.error("Restaurant Billing Series is mandatory.");
        setSaving(false);
        return;
      }

      // 2c. Activity Billing Check (Mandatory if Billing Enabled)
      const actBilling = form.activity_billing;
      if (!actBilling?.prefix || actBilling?.start_number === undefined || actBilling?.start_number === "") {
        toast.error("Activity Billing Series is mandatory.");
        setSaving(false);
        return;
      }
    }
    // --- VALIDATION END ---

    try {
      const sanitize = (obj) => {
        if (!obj) return obj;
        const { current_number, ...rest } = obj;
        return rest;
      };

      const payload = {
        hotel_billing: Object.fromEntries(
          Object.entries(form.hotel_billing).map(([k, v]) => [
            k,
            sanitize(v),
          ])
        ),
        restaurant_billing: sanitize(form.restaurant_billing),
        activity_billing: sanitize(form.activity_billing),
        advance_receipt: sanitize(form.advance_receipt),
      };

      await post("", {
        query: ADD_INVOICE_SERIES_SETTINGS,
        variables: payload,
      });

      toast.success("Invoice series updated successfully.");
      setEditMode(false);
      setDbSnapshot(form);
    } catch (err) {
      toast.error("Save failed.");
      console.error(err);
    }

    setSaving(false);
  };

  /* ----------------------------------------------------
       Handlers
     ----------------------------------------------------- */
  const handleHotelChange = (seriesKey, field, value) => {
    if (!editMode) return;
    setForm((prev) => ({
      ...prev,
      hotel_billing: {
        ...prev.hotel_billing,
        [seriesKey]: {
          ...prev.hotel_billing?.[seriesKey],
          [field]: value,
        },
      },
    }));
  };

  const handleSimpleChange = (section, field, value) => {
    if (!editMode) return;
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  /* ----------------------------------------------------
       UI
     ----------------------------------------------------- */
  return (
    <Accordion
      onChange={(e, expanded) => expanded && loadSeries()}
      sx={{ mt: 1, backgroundColor: colors.primary[400] }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h4" fontWeight={600} color={colors.greenAccent[600]}>
          Billing – Invoice Series
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>
        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={22} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            
            {/* Show Hotel, Restaurant, Activity ONLY if billing is enabled */}
            {billingEnabled && (
              <>
                {/* HOTEL BILLING */}
                <Stack>
                  <Typography variant="h5" fontWeight={700} mb={1}>
                    Hotel Billing
                  </Typography>

                  {HOTEL_SERIES.map((series) => (
                    <SeriesEditor
                      key={series}
                      label={series}
                      data={form.hotel_billing?.[series]}
                      disabled={!editMode}
                      onChange={(field, value) =>
                        handleHotelChange(series, field, value)
                      }
                    />
                  ))}
                </Stack>

                {/* RESTAURANT */}
                <Stack>
                  <Typography variant="h5" fontWeight={700} mb={1}>
                    Restaurant Billing
                  </Typography>

                  <SeriesEditor
                    label="Restaurant"
                    data={form.restaurant_billing}
                    disabled={!editMode}
                    onChange={(field, value) =>
                      handleSimpleChange("restaurant_billing", field, value)
                    }
                  />
                </Stack>

                {/* ACTIVITY */}
                <Stack>
                  <Typography variant="h5" fontWeight={700} mb={1}>
                    Activity Billing
                  </Typography>

                  <SeriesEditor
                    label="Activity"
                    data={form.activity_billing}
                    disabled={!editMode}
                    onChange={(field, value) =>
                      handleSimpleChange("activity_billing", field, value)
                    }
                  />
                </Stack>
              </>
            )}

            {/* ADVANCE RECEIPT (ALWAYS VISIBLE & MANDATORY) */}
            <Stack>
              <Typography variant="h5" fontWeight={700} mb={1}>
                Advance Receipt
              </Typography>

              <SeriesEditor
                label="Advance Receipt"
                data={form.advance_receipt}
                disabled={!editMode}
                onChange={(field, value) =>
                  handleSimpleChange("advance_receipt", field, value)
                }
              />
            </Stack>

            {/* ACTIONS */}
            {!editMode ? (
              <Button variant="contained" onClick={() => setEditMode(true)} sx={{ width: 120 }}>
                Edit
              </Button>
            ) : (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={saveSeries}
                  sx={{ width: 120 }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>

                <Button
                  variant="outlined"
                  disabled={saving}
                  onClick={() => {
                    if (dbSnapshot) setForm(dbSnapshot);
                    setEditMode(false);
                  }}
                  sx={{ width: 120 }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};